import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSupplierRoster,
  invalidateSupplierRosterCache,
} from '../services/supplierRosterCache';
import { SUPPLIER_DETAIL_SELECT, SUPPLIER_LIST_SELECT } from '../services/supplierSelect';
import { supabase } from '../services/supabase';
import { toErrorMessage } from '../utils/toErrorMessage';
import { hideSimulatedSuppliers } from '../apps/early-signals/simulatedSuppliers';
import { useAuth } from './useAuth';
import type { Supplier } from '../types';

export type UseSuppliersOptions = {
  /** When true and user is admin, load all suppliers (no company filter). */
  scope?: 'all';
};

function throwQueryError(error: unknown): never {
  throw error instanceof Error ? error : new Error(toErrorMessage(error, 'Request failed'));
}

function invalidateSupplierQueries(queryClient: ReturnType<typeof useQueryClient>) {
  invalidateSupplierRosterCache();
  queryClient.invalidateQueries({ queryKey: ['suppliers'] });
}

export function useSuppliers(options?: UseSuppliersOptions) {
  const { companyId, effectiveCompanyId, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const adminScopeAll = !!(options?.scope === 'all' && isAdmin);

  const query = useQuery<Supplier[]>({
    queryKey: ['suppliers', adminScopeAll ? 'all' : companyId, effectiveCompanyId ?? 'none'],
    queryFn: async () => {
      // RLS via supplier_companies; slim list select + shared session cache with Risk Analyst.
      // Simulated Supplier N rows belong to The Simple Intel and stay off every other company.
      const rows = await fetchSupplierRoster({ select: SUPPLIER_LIST_SELECT });
      return hideSimulatedSuppliers(rows, effectiveCompanyId);
    },
    enabled: true,
    staleTime: 60000,
    retry: 1,
  });

  const insertMutation = useMutation({
    mutationFn: async (row: Partial<Supplier>) => {
      const { data, error } = await supabase.from('suppliers').insert(row).select(SUPPLIER_DETAIL_SELECT).single();
      if (error) throwQueryError(error);
      return data as unknown as Supplier;
    },
    onSuccess: () => {
      invalidateSupplierQueries(queryClient);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Supplier> & { id: string }) => {
      const { data, error } = await supabase
        .from('suppliers')
        .update({ ...updates, embedding: null })
        .eq('id', id)
        .select(SUPPLIER_DETAIL_SELECT)
        .single();
      if (error) throwQueryError(error);
      return data as unknown as Supplier;
    },
    onSuccess: () => {
      invalidateSupplierQueries(queryClient);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('suppliers').delete().eq('id', id);
      if (error) throwQueryError(error);
    },
    onSuccess: () => {
      invalidateSupplierQueries(queryClient);
    },
  });

  return {
    suppliers: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    insertSupplier: insertMutation.mutateAsync,
    updateSupplier: updateMutation.mutateAsync,
    deleteSupplier: deleteMutation.mutateAsync,
  };
}
